import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import Highlight from "@tiptap/extension-highlight";
import Color from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import {
  MdFormatBold,
  MdFormatItalic,
  MdFormatUnderlined,
  MdStrikethroughS,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdFormatQuote,
  MdCode,
  MdHorizontalRule,
  MdFormatAlignLeft,
  MdFormatAlignCenter,
  MdFormatAlignRight,
  MdLink,
  MdLinkOff,
  MdImage,
  MdUndo,
  MdRedo,
  MdFormatColorText,
  MdFormatColorFill,
  MdOutlineFormatClear,
} from "react-icons/md";

const RichTextEditor = ({ value, onChange }) => {
  const [, setToolbarTick] = useState(0);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        code: true,
        codeBlock: true,
        horizontalRule: true,
      }),
      Underline,
      Link.configure({
  openOnClick: true,
  HTMLAttributes: {
    target: "_blank",
    rel: "noopener noreferrer",
  },
}),
      Image,
      Highlight.configure({ multicolor: true }),
      TextStyle,
      Color,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Placeholder.configure({
        placeholder: "Write detailed comments...",
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    onSelectionUpdate: () => {
      setToolbarTick((n) => n + 1);
    },
    onTransaction: () => {
      setToolbarTick((n) => n + 1);
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "");
    }
  }, [value, editor]);

  const colorInputRef = useRef(null);

  if (!editor) return null;

  const run = (fn) => () => fn(editor.chain().focus()).run();
  const btnClass = (isActive) => `rte-icon-btn ${isActive ? "active" : ""}`;
  const applySelectionStyle = (applyFn) => () => {
    applyFn(editor.chain().focus()).run();
  };

  // wrap the current selection (or insert quotes if collapsed) for inline quotation
  const wrapWithQuotes = () => {
    const { from, to, empty } = editor.state.selection;
    if (empty) {
      editor.chain().focus().insertContent('""').run();
      // place cursor between quotes
      editor.commands.setTextSelection({ from: from + 1, to: from + 1 });
    } else {
      const selected = editor.state.doc.textBetween(from, to);
      editor.chain().focus().insertContentAt({ from, to }, `"${selected}"`).run();
    }
  };

const setLink = () => {
  const url = prompt("Enter link URL");
  const trimmedUrl = url?.trim();
  if (!trimmedUrl) return;

  const { empty } = editor.state.selection;
  if (empty) {
    editor
      .chain()
      .focus()
      .insertContent(
        `<a href="${trimmedUrl}" target="_blank" rel="noopener noreferrer">link</a>`
      )
      .run();
    return;
  }

  editor
    .chain()
    .focus()
    .extendMarkRange("link")
    .setLink({
      href: trimmedUrl,
      target: "_blank",
      rel: "noopener noreferrer",
    })
    .run();
};
  const addImage = () => {
    const url = prompt("Enter image URL");
    const trimmedUrl = url?.trim();
    if (!trimmedUrl) return;

    editor
      .chain()
      .focus()
      .insertContent(
        `<a href="${trimmedUrl}" target="_blank" rel="noopener noreferrer">image</a>`
      )
      .run();
  };

  const unsetLink = () => {
    if (!editor.isActive("link")) return;

    editor.chain().focus().extendMarkRange("link").run();
    const { from, to } = editor.state.selection;
    const selected = editor.state.doc
      .textBetween(from, to, " ", "")
      .trim()
      .toLowerCase();

    if (selected === "link" || selected === "image") {
      editor.chain().focus().deleteRange({ from, to }).run();
      return;
    }

    editor.chain().focus().unsetLink().run();
  };

  const changeBlockType = (type) => {
    if (type === "paragraph") {
      editor.chain().focus().setParagraph().run();
      return;
    }
    editor.chain().focus().toggleHeading({ level: Number(type) }).run();
  };

  const currentBlock = editor.isActive("heading", { level: 1 })
    ? "1"
    : editor.isActive("heading", { level: 2 })
      ? "2"
      : editor.isActive("heading", { level: 3 })
        ? "3"
        : "paragraph";

  const setTextColor = (color) => {
    const { from, to } = editor.state.selection;

    if (from !== to) {
      editor
        .chain()
        .focus()
        .setColor(color)
        .setTextSelection(to)
        .unsetColor()
        .run();
      return;
    }

    editor.chain().focus().setColor(color).run();
  };

  const setHighlight = (color) => {
    const { from, to } = editor.state.selection;

    if (from !== to) {
      editor
        .chain()
        .focus()
        .toggleHighlight({ color })
        .setTextSelection(to)
        .unsetHighlight()
        .run();
      return;
    }

    editor.chain().focus().toggleHighlight({ color }).run();
  };

  const openColorPicker = () => {
    colorInputRef.current?.click();
  };

  return (
    <div className="rte-wrapper">
      <div className="rte-toolbar">
        <div className="rte-group">
          <select
            className="rte-select"
            value={currentBlock}
            onChange={(e) => changeBlockType(e.target.value)}
            title="Text style"
          >
            <option value="paragraph">Paragraph</option>
            <option value="1">Heading 1</option>
            <option value="2">Heading 2</option>
            <option value="3">Heading 3</option>
          </select>
          <button type="button" className={btnClass(editor.isActive("bold"))} onClick={applySelectionStyle((c) => c.toggleBold())} title="Bold"><MdFormatBold /></button>
          <button type="button" className={btnClass(editor.isActive("italic"))} onClick={applySelectionStyle((c) => c.toggleItalic())} title="Italic"><MdFormatItalic /></button>
          <button type="button" className={btnClass(editor.isActive("underline"))} onClick={applySelectionStyle((c) => c.toggleUnderline())} title="Underline"><MdFormatUnderlined /></button>
          <button type="button" className={btnClass(editor.isActive("strike"))} onClick={applySelectionStyle((c) => c.toggleStrike())} title="Strikethrough"><MdStrikethroughS /></button>
          <button type="button" className="rte-icon-btn" onClick={wrapWithQuotes} title="Quote selection">"</button>
          <button type="button" className={btnClass(editor.isActive("code"))} onClick={applySelectionStyle((c) => c.toggleCode())} title="Inline code"><MdCode /></button>
        </div>

        <span className="rte-sep" />

        <div className="rte-group">
          <button type="button" className={btnClass(editor.isActive("bulletList"))} onClick={run((c) => c.toggleBulletList())} title="Bulleted list"><MdFormatListBulleted /></button>
          <button type="button" className={btnClass(editor.isActive("orderedList"))} onClick={run((c) => c.toggleOrderedList())} title="Numbered list"><MdFormatListNumbered /></button>
          <button type="button" className={btnClass(editor.isActive("blockquote"))} onClick={run((c) => c.toggleBlockquote())} title="Hghlights"><MdFormatQuote /></button>
          <button type="button" className="rte-icon-btn" onClick={run((c) => c.setHorizontalRule())} title="Horizontal line"><MdHorizontalRule /></button>
          <button type="button" className={btnClass(editor.isActive({ textAlign: "left" }))} onClick={run((c) => c.setTextAlign("left"))} title="Align left"><MdFormatAlignLeft /></button>
          <button type="button" className={btnClass(editor.isActive({ textAlign: "center" }))} onClick={run((c) => c.setTextAlign("center"))} title="Align center"><MdFormatAlignCenter /></button>
          <button type="button" className={btnClass(editor.isActive({ textAlign: "right" }))} onClick={run((c) => c.setTextAlign("right"))} title="Align right"><MdFormatAlignRight /></button>
        </div>

        <span className="rte-sep" />

        <div className="rte-group">
          <button type="button" className={btnClass(editor.isActive("link"))} onClick={setLink} title="Insert link"><MdLink /></button>
          <button type="button" className="rte-icon-btn" onClick={unsetLink} disabled={!editor.isActive("link")} title="Remove link"><MdLinkOff /></button>
          <button type="button" className="rte-icon-btn" onClick={addImage} title="Insert image"><MdImage /></button>
        </div>

        <span className="rte-sep" />

        <div className="rte-group">
          <button type="button" className="rte-icon-btn" onClick={openColorPicker} title="Choose text color">
            <MdFormatColorText />
          </button>
          <input
            ref={colorInputRef}
            type="color"
            className="rte-color-picker-input"
            defaultValue="#6D1CB2"
            onChange={(e) => setTextColor(e.target.value)}
            aria-label="Choose text color"
          />
          <button type="button" className={btnClass(editor.isActive("highlight"))} onClick={() => setHighlight("#FFF59D")} title="Highlight yellow"><MdFormatColorFill /></button>
        </div>

        <span className="rte-sep" />

        <div className="rte-group">
          <button type="button" className="rte-icon-btn" onClick={run((c) => c.unsetAllMarks())} title="Clear formatting"><MdOutlineFormatClear /></button>
          <button type="button" className="rte-icon-btn" onClick={run((c) => c.undo())} disabled={!editor.can().undo()} title="Undo"><MdUndo /></button>
          <button type="button" className="rte-icon-btn" onClick={run((c) => c.redo())} disabled={!editor.can().redo()} title="Redo"><MdRedo /></button>
        </div>
      </div>

      <EditorContent editor={editor} className="rte-editor" />
    </div>
  );
};

export default RichTextEditor;
